import React, { useEffect, useMemo, useRef, memo } from 'react';
import './GlobeViewer.css';

const COUNTRY_ALIASES = {
  'United States': ['United States', 'USA', 'US', 'United States of America'],
  'United Kingdom': ['United Kingdom', 'UK', 'Great Britain'],
  Spain: ['Spain', 'España', 'Espana'],
  Germany: ['Germany'],
  Sweden: ['Sweden'],
  Switzerland: ['Switzerland', 'Swiss'],
  Czechia: ['Czechia', 'Czech Republic'],
  Netherlands: ['Netherlands'],
  Austria: ['Austria'],
  Belgium: ['Belgium', 'Belgique', 'België', 'Belgie'],
  Italy: ['Italy', 'Italia'],
  'United States of America': ['United States', 'USA', 'US', 'United States of America']
};

function GlobeViewer({ location, markers, activeMarkerId, focusedMarkerId, onMarkerClick, isBooting, hasSearched, isSearching, highlightAllMarkers }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const dataSourceRef = useRef(null);
  const handlerRef = useRef(null);
  const onMarkerClickRef = useRef(onMarkerClick);
  const markerMapRef = useRef(new Map());
  const hoveredMarkerIdRef = useRef('');
  const lastFocusedMarkerRef = useRef('');
  const cameraHeightRef = useRef(19000000);
  const searchLightsRef = useRef([]);
  const animationFrameRef = useRef(null);
  const sceneErrorHandlerRef = useRef(null);
  const activeMarkerIdRef = useRef(activeMarkerId);
  const hasSearchedRef = useRef(hasSearched);
  const markerCountRef = useRef(0);

  const renderMarkers = useMemo(() => {
    if (!hasSearched) return markers.slice(0, 900);
    if (markers.length > 3600) return markers.slice(0, 2600);
    if (markers.length > 2000) return markers.slice(0, 2200);
    if (markers.length > 1200) return markers.slice(0, 1600);
    return markers;
  }, [markers, hasSearched]);

  useEffect(() => {
    activeMarkerIdRef.current = activeMarkerId;
    hasSearchedRef.current = hasSearched;
    markerCountRef.current = renderMarkers.length;
  }, [activeMarkerId, hasSearched, renderMarkers.length]);

  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || !Cesium || !isSearching) {
      return;
    }

    const lightCount = 12;
    const lights = [];

    for (let i = 0; i < lightCount; i++) {
      const light = {
        id: `search-light-${i}`,
        index: i,
        startTime: Date.now(),
        entity: null
      };
      lights.push(light);
    }

    searchLightsRef.current = lights;

    const animateSearchLights = () => {
      if (!viewerRef.current || viewerRef.current.isDestroyed()) {
        return;
      }

      const now = Date.now();
      const duration = 3200;

      lights.forEach(light => {
        const elapsed = (now - light.startTime) % duration;
        const progress = elapsed / duration;
        const angle = progress * Math.PI * 2;
        const stagger = (light.index / lightCount) * Math.PI * 2;
        const finalAngle = angle + stagger;

        const latitude = Math.cos(finalAngle) * 32;
        const longitude = Math.sin(finalAngle) * 52;
        const altitude = 20500000 + Math.sin(progress * Math.PI * 2) * 1800000;

        const position = Cesium.Cartesian3.fromDegrees(longitude, latitude, altitude);

        if (!light.entity) {
          light.entity = viewer.entities.add({
            position: position,
            point: {
              pixelSize: 8,
              color: Cesium.Color.fromCssColorString('#00ffaa').withAlpha(0.85),
              outlineColor: Cesium.Color.fromCssColorString('#00dd88').withAlpha(1.0),
              outlineWidth: 2,
              disableDepthTestDistance: Number.POSITIVE_INFINITY
            },
            ellipse: {
              semiMajorAxis: 420000,
              semiMinorAxis: 420000,
              height: 0,
              material: new Cesium.ColorMaterialProperty(
                new Cesium.CallbackProperty(() => {
                  const alpha = 0.28 + Math.sin(progress * Math.PI * 3.5) * 0.15;
                  return Cesium.Color.fromCssColorString('#00ffaa').withAlpha(Math.max(0.15, alpha));
                }, false)
              ),
              outline: true,
              outlineColor: Cesium.Color.fromCssColorString('#00ffaa').withAlpha(0.45),
              outlineWidth: 1.5,
              disableDepthTestDistance: Number.POSITIVE_INFINITY
            }
          });
        } else {
          light.entity.position = position;
          const pointAlpha = 0.5 + Math.sin(progress * Math.PI * 3.5) * 0.35;
          if (light.entity.point) {
            light.entity.point.color = Cesium.Color.fromCssColorString('#00ffaa').withAlpha(Math.max(0.4, pointAlpha));
          }
        }
      });

      viewer.scene.requestRender();
      animationFrameRef.current = requestAnimationFrame(animateSearchLights);
    };

    animationFrameRef.current = requestAnimationFrame(animateSearchLights);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      lights.forEach(light => {
        if (light.entity && viewer.entities.contains(light.entity)) {
          viewer.entities.remove(light.entity);
        }
      });
      searchLightsRef.current = [];
    };
  }, [isSearching]);

  useEffect(() => {
    if (!window.Cesium || viewerRef.current || !containerRef.current) {
      return undefined;
    }

    const Cesium = window.Cesium;
    const viewer = new Cesium.Viewer(containerRef.current, {
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      animation: false,
      timeline: false,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      shouldAnimate: true,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      skyBox: false,
      skyAtmosphere: false,
      shadows: false,
      requestRenderMode: true,
      maximumRenderTimeChange: Infinity
    });

    viewer.scene.rethrowRenderErrors = false;
    const onSceneRenderError = () => {
      if (!viewerRef.current || viewerRef.current.isDestroyed()) {
        return;
      }
      viewerRef.current.scene.requestRender();
    };
    viewer.scene.renderError.addEventListener(onSceneRenderError);
    sceneErrorHandlerRef.current = onSceneRenderError;

    viewer.imageryLayers.removeAll();
    viewer.resolutionScale = Math.min(window.devicePixelRatio || 1, 1.0);
    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString('#020103');
    viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString('#03010a');
    viewer.scene.globe.depthTestAgainstTerrain = false;
    viewer.scene.globe.showGroundAtmosphere = false;
    viewer.scene.globe.enableLighting = false;
    viewer.scene.globe.tileCacheSize = 96;
    viewer.scene.fxaa = false;
    viewer.scene.highDynamicRange = false;
    viewer.scene.postProcessStages.fxaa.enabled = false;
    viewer.scene.screenSpaceCameraController.enableTilt = false;
    viewer.scene.screenSpaceCameraController.enableLook = false;
    viewer.scene.screenSpaceCameraController.inertiaSpin = 0.84;
    viewer.scene.screenSpaceCameraController.inertiaTranslate = 0.86;
    viewer.scene.screenSpaceCameraController.inertiaZoom = 0.72;
    viewer.scene.screenSpaceCameraController.minimumZoomDistance = 1650000;
    viewer.scene.screenSpaceCameraController.maximumZoomDistance = 26000000;
    viewer.scene.screenSpaceCameraController.maximumTiltAngle = 0;
    viewer.cesiumWidget.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(5, 18, 19000000)
    });

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction(movement => {
      const picked = viewer.scene.pick(movement.position);
      const entity = picked?.id;
      const cameraId = entity?.properties?.cameraId?.getValue?.();
      if (cameraId) {
        onMarkerClickRef.current?.(cameraId);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction(movement => {
      const picked = viewer.scene.pick(movement.endPosition);
      const entity = picked?.id;
      const cameraId = entity?.properties?.cameraId?.getValue?.() || '';
      if (cameraId === hoveredMarkerIdRef.current) {
        return;
      }

      hoveredMarkerIdRef.current = cameraId;
      syncMarkerStyles(viewer, markerMapRef.current, {
        activeMarkerId: activeMarkerIdRef.current,
        hoveredMarkerId: cameraId,
        hasSearched: hasSearchedRef.current,
        markerCount: markerCountRef.current
      });
      viewer.scene.requestRender();
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    viewerRef.current = viewer;
    handlerRef.current = handler;

    let active = true;

    fetch('/data/countries.geo.json')
      .then(response => response.json())
      .then(geojson => Cesium.GeoJsonDataSource.load(geojson, {
        stroke: Cesium.Color.fromCssColorString('rgba(184, 99, 255, 0.2)'),
        fill: Cesium.Color.TRANSPARENT,
        strokeWidth: 1,
        clampToGround: false
      }))
      .then(dataSource => {
        if (!active || !viewerRef.current || viewerRef.current.isDestroyed()) {
          return;
        }

        dataSourceRef.current = dataSource;
        viewerRef.current.dataSources.add(dataSource);
        dataSource.entities.values.forEach(entity => {
          if (entity.polygon) {
            entity.polygon.material = Cesium.Color.TRANSPARENT;
            entity.polygon.outline = true;
            entity.polygon.outlineColor = Cesium.Color.fromCssColorString('rgba(140, 79, 223, 0.16)');
          }
        });
        viewer.scene.requestRender();
      })
      .catch(() => {});

    return () => {
      active = false;
      const currentHandler = handlerRef.current;
      const currentViewer = viewerRef.current;

      markerMapRef.current = new Map();
      dataSourceRef.current = null;
      handlerRef.current = null;
      viewerRef.current = null;
      hoveredMarkerIdRef.current = '';
      lastFocusedMarkerRef.current = '';

      if (currentHandler && !currentHandler.isDestroyed()) {
        currentHandler.destroy();
      }

      if (currentViewer && !currentViewer.isDestroyed()) {
        const sceneErrorHandler = sceneErrorHandlerRef.current;
        if (sceneErrorHandler) {
          currentViewer.scene.renderError.removeEventListener(sceneErrorHandler);
          sceneErrorHandlerRef.current = null;
        }
        try {
          currentViewer.destroy();
        } catch (error) {
        }
      }
    };
  }, []);

  useEffect(() => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || !Cesium) {
      return;
    }

    viewer.entities.removeAll();
    markerMapRef.current = new Map();

    renderMarkers.forEach(marker => {
      if (!Number.isFinite(marker.latitude) || !Number.isFinite(marker.longitude)) {
        return;
      }
      const elevatedPosition = Cesium.Cartesian3.fromDegrees(marker.longitude, marker.latitude, hasSearched ? 180000 : 90000);
      const basePosition = Cesium.Cartesian3.fromDegrees(marker.longitude, marker.latitude, 0);
      const entity = viewer.entities.add({
        id: marker.id,
        position: elevatedPosition,
        polyline: {
          positions: [basePosition, elevatedPosition],
          width: hasSearched ? 1.8 : 1.3,
          material: Cesium.Color.fromCssColorString(hasSearched ? '#ff365f' : '#b667ff').withAlpha(hasSearched ? 0.62 : 0.38),
          disableDepthTestDistance: Number.POSITIVE_INFINITY
        },
        ellipse: {
          semiMajorAxis: hasSearched ? 52000 : 42000,
          semiMinorAxis: hasSearched ? 52000 : 42000,
          height: 0,
          material: highlightAllMarkers
          ? new Cesium.ColorMaterialProperty(
              new Cesium.CallbackProperty(() => {
                const t = (Date.now() % 900) / 900;
                const alpha = (hasSearched ? 0.10 : 0.085) + Math.sin(t * Math.PI * 2) * 0.035;
                return Cesium.Color.fromCssColorString(hasSearched ? '#ff365f' : '#9a48ff').withAlpha(alpha);
              }, false)
            )
          : Cesium.Color.fromCssColorString(hasSearched ? '#ff365f' : '#9a48ff').withAlpha(hasSearched ? 0.09 : 0.08),
          outline: false,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new Cesium.NearFarScalar(1000000, 1, 18000000, 0.14)
        },
        point: {
          pixelSize: hasSearched ? 11 : 9,
          color: highlightAllMarkers
          ? new Cesium.CallbackProperty(() => {
              const t = (Date.now() % 900) / 900;
              const alpha = 0.55 + Math.sin(t * Math.PI * 2) * 0.35;
              return Cesium.Color.fromCssColorString(hasSearched ? '#ff365f' : '#bf79ff').withAlpha(alpha);
            }, false)
          : Cesium.Color.fromCssColorString(hasSearched ? '#ff365f' : '#bf79ff'),
          outlineColor: Cesium.Color.fromCssColorString('#14031f'),
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new Cesium.NearFarScalar(1000000, 1.1, 18000000, 0.4)
        },
        label: hasSearched
          ? {
              text: marker.title,
              show: false,
              font: '600 13px Inter, sans-serif',
              style: Cesium.LabelStyle.FILL_AND_OUTLINE,
              fillColor: Cesium.Color.WHITE,
              outlineColor: Cesium.Color.fromCssColorString('#08010c'),
              outlineWidth: 5,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              pixelOffset: new Cesium.Cartesian2(0, -18),
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              scaleByDistance: new Cesium.NearFarScalar(1000000, 1.0, 15000000, 0.0)
            }
          : undefined,
        properties: {
          cameraId: marker.id,
          title: marker.title,
          availabilityStatus: marker.availabilityStatus || 'unknown',
          latitude: marker.latitude,
          longitude: marker.longitude
        }
      });

      markerMapRef.current.set(marker.id, entity);
    });

    syncMarkerStyles(viewer, markerMapRef.current, {
      activeMarkerId,
      hoveredMarkerId: hoveredMarkerIdRef.current,
      hasSearched,
      markerCount: renderMarkers.length
    });
    viewer.scene.requestRender();
  }, [activeMarkerId, hasSearched, renderMarkers, highlightAllMarkers]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) {
      return;
    }

    syncMarkerStyles(viewer, markerMapRef.current, {
      activeMarkerId,
      hoveredMarkerId: hoveredMarkerIdRef.current,
      hasSearched,
      markerCount: renderMarkers.length
    });
    viewer.scene.requestRender();
  }, [activeMarkerId, hasSearched, renderMarkers.length]);

  useEffect(() => {
    const dataSource = dataSourceRef.current;
    const Cesium = window.Cesium;
    if (!dataSource || !Cesium) {
      return;
    }

    const activeCountries = new Set();
    renderMarkers.forEach(marker => {
      const aliases = COUNTRY_ALIASES[marker.country] || [marker.country];
      aliases.filter(Boolean).forEach(alias => activeCountries.add(String(alias).toLowerCase()));
    });

    dataSource.entities.values.forEach(entity => {
      const name = entity.properties?.name?.getValue?.() || entity.name || '';
      const iso = entity.properties?.iso_a3?.getValue?.() || '';
      const isActiveCountry = activeCountries.has(String(name).toLowerCase()) || activeCountries.has(String(iso).toLowerCase());

      if (entity.polygon) {
        entity.polygon.outlineColor = isActiveCountry
          ? Cesium.Color.fromCssColorString('rgba(192, 125, 255, 0.9)')
          : Cesium.Color.fromCssColorString('rgba(145, 91, 194, 0.14)');
      }

      if (!entity.label) {
        entity.label = new Cesium.LabelGraphics();
      }
      entity.label.text = name;
      entity.label.show = false;
      entity.label.font = '400 11px Inter, sans-serif';
      entity.label.style = Cesium.LabelStyle.FILL_AND_OUTLINE;
      entity.label.fillColor = Cesium.Color.fromCssColorString('rgba(220, 200, 240, 0.5)');
      entity.label.outlineColor = Cesium.Color.fromCssColorString('rgba(8, 1, 12, 0.7)');
      entity.label.outlineWidth = 3;
      entity.label.verticalOrigin = Cesium.VerticalOrigin.CENTER;
      entity.label.horizontalOrigin = Cesium.HorizontalOrigin.CENTER;
      entity.label.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      entity.label.scaleByDistance = new Cesium.NearFarScalar(4500000, 0.7, 9000000, 0.0);
    });

    viewerRef.current?.scene.requestRender();
  }, [renderMarkers]);

  useEffect(() => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    const dataSource = dataSourceRef.current;
    if (!viewer || !Cesium || !dataSource) {
      return;
    }

    const updateCountryLabels = () => {
      const cameraHeight = cameraHeightRef.current;
      const showLabels = cameraHeight > 4500000 && cameraHeight < 9000000;

      dataSource.entities.values.forEach(entity => {
        if (entity.label) {
          entity.label.show = showLabels;
        }
      });

      viewer.scene.requestRender();
    };

    const onCameraChange = () => {
      const cameraCartographic = Cesium.Cartographic.fromCartesian(viewer.camera.position);
      const height = cameraCartographic ? cameraCartographic.height : 19000000;
      cameraHeightRef.current = height;
      updateCountryLabels();
    };

    viewer.camera.changed.addEventListener(onCameraChange);

    return () => {
      viewer.camera.changed.removeEventListener(onCameraChange);
    };
  }, [renderMarkers.length, hasSearched, highlightAllMarkers]);

  useEffect(() => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    if (!viewer || !Cesium || !location) {
      return;
    }

    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(location.longitude, location.latitude, getSearchAltitude(location, renderMarkers.length, hasSearched)),
      duration: hasSearched ? 1.35 : 1.8,
      easingFunction: Cesium.EasingFunction.QUADRATIC_OUT
    });
  }, [hasSearched, location, renderMarkers.length]);

  useEffect(() => {
    const viewer = viewerRef.current;
    const Cesium = window.Cesium;
    const entity = focusedMarkerId ? markerMapRef.current.get(focusedMarkerId) : null;

    if (!focusedMarkerId) {
      lastFocusedMarkerRef.current = '';
      return;
    }

    if (!viewer || !Cesium || !entity || !hasSearched || lastFocusedMarkerRef.current === focusedMarkerId) {
      return;
    }

    lastFocusedMarkerRef.current = focusedMarkerId;

    const cartographic = Cesium.Cartographic.fromCartesian(entity.position.getValue(Cesium.JulianDate.now()));
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromRadians(cartographic.longitude, cartographic.latitude, getMarkerFocusAltitude(renderMarkers.length)),
      duration: 0.95,
      easingFunction: Cesium.EasingFunction.QUADRATIC_OUT
    });
  }, [focusedMarkerId, hasSearched, renderMarkers.length]);

  return (
    <div className={`globe-viewer ${hasSearched ? 'globe-viewer-active' : 'globe-viewer-idle'}`}>
      <div ref={containerRef} className="cesium-host" />
      <div className="globe-shadow" />
      <div className="globe-aura globe-aura-one" />
      <div className="globe-aura globe-aura-two" />
      {isBooting && <div className="globe-boot-indicator">synchronising</div>}
    </div>
  );
}

function syncMarkerStyles(viewer, markerMap, state) {
  const Cesium = window.Cesium;
  if (!viewer || !Cesium) {
    return;
  }

  markerMap.forEach((entity, markerId) => {
    const isActive = markerId === state.activeMarkerId;
    const isHovered = markerId === state.hoveredMarkerId;
    const isOffline = entity.properties?.availabilityStatus?.getValue?.() === 'offline';
    const baseColor = state.hasSearched ? '#ff365f' : '#bf79ff';
    const hoverColor = state.hasSearched ? '#ff5679' : '#d6a1ff';
    const activeColor = '#ffffff';

    const latitude = entity.properties?.latitude?.getValue?.();
    const longitude = entity.properties?.longitude?.getValue?.();

    if (entity.position && Number.isFinite(latitude) && Number.isFinite(longitude)) {
      entity.position = Cesium.Cartesian3.fromDegrees(
        longitude,
        latitude,
        isActive ? 230000 : isHovered ? 210000 : state.hasSearched ? 180000 : 90000
      );
    }

    if (entity.point) {
      entity.point.pixelSize = isActive ? 17 : isHovered ? 15 : isOffline ? 8 : state.hasSearched ? 11 : 9;
      entity.point.color = isActive
        ? Cesium.Color.fromCssColorString(activeColor)
        : Cesium.Color.fromCssColorString(isHovered ? hoverColor : isOffline ? '#80708f' : baseColor);
      entity.point.outlineColor = isActive
        ? Cesium.Color.fromCssColorString('#a355ff')
        : Cesium.Color.fromCssColorString('#14031f');
      entity.point.outlineWidth = isActive ? 3 : isHovered ? 2.6 : 2;
    }

    if (entity.ellipse) {
      const radius = isActive ? 98000 : isHovered ? 76000 : state.hasSearched ? 52000 : 42000;
      entity.ellipse.semiMajorAxis = radius;
      entity.ellipse.semiMinorAxis = radius;
      entity.ellipse.material = Cesium.Color.fromCssColorString(isActive ? '#ffffff' : isHovered ? hoverColor : baseColor)
        .withAlpha(isActive ? 0.16 : isHovered ? 0.12 : 0.08);
    }

    if (entity.polyline) {
      entity.polyline.width = isActive ? 2.6 : isHovered ? 2.2 : state.hasSearched ? 1.8 : 1.3;
      entity.polyline.material = Cesium.Color.fromCssColorString(isActive ? '#ffffff' : isHovered ? hoverColor : baseColor)
        .withAlpha(isActive ? 0.86 : isHovered ? 0.72 : state.hasSearched ? 0.62 : 0.38);
    }

    if (entity.label) {
      entity.label.show = Boolean(state.hasSearched && (isActive || isHovered) && state.markerCount <= 220);
    }
  });
}

function getSearchAltitude(location, count, hasSearched) {
  if (!hasSearched || location?.isGlobal || count > 1500) {
    return 19000000;
  }
  if (count > 900) {
    return 15500000;
  }
  if (count > 350) {
    return 11500000;
  }
  if (count > 140) {
    return 7800000;
  }
  if (count > 45) {
    return 5000000;
  }
  if (count > 12) {
    return 3200000;
  }
  return 2200000;
}

function getMarkerFocusAltitude(count) {
  if (count > 120) {
    return 5200000;
  }
  if (count > 40) {
    return 3000000;
  }
  if (count > 12) {
    return 2350000;
  }
  return 1650000;
}

export default memo(GlobeViewer);
